import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Shield, UserCheck, UserX, AlertCircle, Users, UserPlus, Trash2, KeyRound } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Badge } from '@/app/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { toast } from '@/app/components/ui/simple-toaster';
import { useUsers, useAssignRole, useNukeUser, useSetSigningPin, useClearSigningPin, useOrgTimeZone } from '@/hooks/use-settings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Label } from '@/app/components/ui/label';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/contexts/auth-context';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import { CompanyRoleSelectItems } from '@/app/components/company-role-select-items';
import { ModuleRoleSyncDialog } from '@/app/components/module-role-sync-dialog';
import { COMPANY_ROLE_OPTIONS } from '@/lib/company-roles';
import { fetchModuleRolesForUser, useBulkUpdateModuleRoles } from '@/hooks/use-admin';
import { usePlatformModules } from '@/hooks/use-modules';
import { getPlatformModules } from '@/lib/modules';
import { companyRoleToModuleRole } from '@/lib/module-roles';
import type { ModuleRole, ModuleRoleLevel, User, UserRole } from '@/lib/types';

export function UserManagementPage() {
  const orgTimeZone = useOrgTimeZone();
  const { user: currentUser } = useAuth();
  const { canManageUsers } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('all');
  const [nukeOpen, setNukeOpen] = useState(false);
  const [nukeTarget, setNukeTarget] = useState<{ id: number; username: string; email: string } | null>(null);
  const [pinDialogUser, setPinDialogUser] = useState<{ id: number; username: string } | null>(null);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [clearPinUser, setClearPinUser] = useState<{ id: number; username: string } | null>(null);
  const [isRoleChangePending, setIsRoleChangePending] = useState(false);
  const [adminPromotion, setAdminPromotion] = useState<{
    user: User;
  } | null>(null);
  const [roleSync, setRoleSync] = useState<{
    userId: number;
    userName: string;
    newRole: UserRole;
    currentRoles: Record<string, ModuleRoleLevel>;
  } | null>(null);
  const [pendingMfaWarning, setPendingMfaWarning] = useState<string | null>(null);

  const filters = {
    search: searchTerm || undefined,
    role: filterRole !== 'all' ? (filterRole as UserRole) : undefined,
    is_active: filterActive !== 'all' ? filterActive === 'true' : undefined,
  };

  const { data: usersData, isLoading, error } = useUsers(filters);
  const {
    enabledModules,
    isLoading: modulesLoading,
    error: modulesError,
  } = usePlatformModules();
  const platformModuleKeys = getPlatformModules().map((module) => module.key);
  const topLevelModules = enabledModules.filter((module) => platformModuleKeys.includes(module));
  const assignRole = useAssignRole();
  const updateModuleRoles = useBulkUpdateModuleRoles();
  const nukeUser = useNukeUser();
  const setPinMutation = useSetSigningPin();
  const clearPinMutation = useClearSigningPin();

  if (!canManageUsers) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Access Denied</h3>
                <p className="text-sm text-red-800 mt-1">
                  You do not have permission to manage users. Only administrators can access this page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleNuke = async () => {
    if (!nukeTarget) return;
    try {
      const response = await nukeUser.mutateAsync(nukeTarget.id);
      toast.success('User Deleted', {
        description: response.account_deleted
          ? `${nukeTarget.username} (${nukeTarget.email}) has been permanently deleted.`
          : `${nukeTarget.username} was removed from this company. Their account is still used by another company.`,
      });
      setNukeOpen(false);
      setNukeTarget(null);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      toast.error('Error', {
        description: axiosErr.response?.data?.error || 'Failed to delete user. Please try again.',
      });
    }
  };

  const handleSetPin = async () => {
    if (!pinDialogUser) return;
    setPinError(null);
    if (pinValue.length !== 6) {
      setPinError('Enter a 6-digit PIN.');
      return;
    }
    try {
      await setPinMutation.mutateAsync({ userId: pinDialogUser.id, pin: pinValue });
      toast.success('PIN Set', { description: `Signing PIN set for ${pinDialogUser.username}.` });
      setPinDialogUser(null);
      setPinValue('');
    } catch (e: unknown) {
      const axiosErr = e as { response?: { data?: { error?: string } } };
      setPinError(axiosErr?.response?.data?.error || 'Failed to set PIN.');
    }
  };

  const handleClearPin = async () => {
    if (!clearPinUser) return;
    try {
      await clearPinMutation.mutateAsync(clearPinUser.id);
      toast.success('PIN Cleared', { description: `Signing PIN cleared for ${clearPinUser.username}.` });
      setClearPinUser(null);
    } catch {
      toast.error('Error', { description: 'Failed to clear PIN.' });
    }
  };

  const applyRoleChange = async (
    user: NonNullable<typeof usersData>['results'][number],
    newRole: UserRole,
    confirmAdminGrant = false,
  ) => {
    if (modulesError) {
      toast.error('Error', {
        description: 'Enabled modules could not be loaded. Please try again.',
      });
      return;
    }

    setIsRoleChangePending(true);
    try {
      let moduleRoles: ModuleRole[];
      try {
        moduleRoles = await fetchModuleRolesForUser(user.id);
      } catch {
        toast.error('Error', {
          description: 'Module access could not be loaded. Please try again.',
        });
        return;
      }

      const response = await assignRole.mutateAsync({
        userId: user.id,
        role: newRole,
        confirmAdminGrant,
      });
      toast.success('Role Updated', {
        description: `User role has been changed to ${newRole}.`,
      });
      setPendingMfaWarning(response.mfa_warning ?? null);
      const currentRoles = Object.fromEntries(
        topLevelModules.map((moduleKey) => {
          const moduleRole = moduleRoles.find(
            (item) => item.user === user.id && item.module === moduleKey,
          );
          return [moduleKey, moduleRole?.role ?? 'no_access'];
        }),
      ) as Record<string, ModuleRoleLevel>;
      setRoleSync({
        userId: user.id,
        userName: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username,
        newRole,
        currentRoles,
      });
    } catch (_err) {
      toast.error('Error', {
        description: 'Failed to update user role. Please try again.',
      });
    } finally {
      setIsRoleChangePending(false);
    }
  };

  const handleRoleChange = (
    user: NonNullable<typeof usersData>['results'][number],
    newRole: UserRole,
  ) => {
    if (newRole === user.role) return;
    if (currentUser?.id === user.id) {
      toast.error('Error', {
        description: 'You cannot change your own company role.',
      });
      return;
    }
    if (newRole === 'Admin' && user.role !== 'Admin') {
      setAdminPromotion({ user });
      return;
    }
    void applyRoleChange(user, newRole);
  };

  const handleUpdateModules = async () => {
    if (!roleSync) return;
    const newModuleRole = companyRoleToModuleRole(roleSync.newRole);
    try {
      const response = await updateModuleRoles.mutateAsync({
        user: roleSync.userId,
        roles: Object.fromEntries(
          topLevelModules.map((moduleKey) => [moduleKey, newModuleRole]),
        ),
      });
      toast.success('Module Access Updated', {
        description: `${roleSync.userName}'s enabled modules now match ${roleSync.newRole}.`,
      });
      setRoleSync(null);
      const warnings = [pendingMfaWarning, response.mfa_warning]
        .filter((warning): warning is string => Boolean(warning))
        .filter((warning, index, allWarnings) => allWarnings.indexOf(warning) === index);
      warnings.forEach((warning) => toast.warning(warning, { duration: 10000 }));
      setPendingMfaWarning(null);
    } catch {
      toast.error('Error', {
        description: 'Failed to update module access. Please try again.',
      });
    }
  };

  const getRoleBadge = (role: UserRole | null) => {
    switch (role) {
      case 'Admin':
        return <Badge className="bg-purple-600">Admin</Badge>;
      case 'Manager':
        return <Badge className="bg-blue-600">Manager</Badge>;
      case 'Inspector':
        return <Badge className="bg-orange-600">Inspector</Badge>;
      case 'Supervisor':
        return <Badge className="bg-teal-600">Supervisor</Badge>;
      case 'User':
        return <Badge className="bg-green-600">User</Badge>;
      case 'Viewer':
        return <Badge variant="secondary">Viewer</Badge>;
      default:
        return <Badge variant="outline">No Role</Badge>;
    }
  };

  const users = usersData?.results || [];

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Users</h3>
                <p className="text-sm text-red-800 mt-1">
                  Failed to load user list. Please try refreshing the page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb items={[
        { label: 'Settings', href: '/settings' },
        { label: 'User Management' },
      ]} />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">User Management</h1>
        <p className="text-gray-600 dark:text-muted-foreground mt-1">Manage user accounts and permissions</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Shield className="h-4 w-4 text-gray-600 dark:text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usersData?.count || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <UserCheck className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => u.is_active).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inactive Users</CardTitle>
            <UserX className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => !u.is_active).length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>View and manage user accounts in your organization</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by username or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full lg:w-[180px]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {COMPANY_ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger className="w-full lg:w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>PIN</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12">
                      <div className="flex flex-col items-center text-center">
                        <Users className="h-12 w-12 text-gray-300 mb-3" />
                        <p className="text-gray-600 dark:text-muted-foreground font-medium">No users found</p>
                        <p className="text-sm text-gray-500 dark:text-muted-foreground mt-1">
                          {searchTerm || filterRole !== 'all' || filterActive !== 'all'
                            ? 'Try adjusting your search or filters'
                            : 'Invite team members to get started'}
                        </p>
                        {!searchTerm && filterRole === 'all' && filterActive === 'all' && (
                          <Button asChild variant="outline" className="mt-4">
                            <Link to="/settings/invitations">
                              <UserPlus className="h-4 w-4 mr-2" />
                              Invite Users
                            </Link>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Select
                          value={user.role || 'none'}
                          onValueChange={(value) => handleRoleChange(user, value as UserRole)}
                          disabled={
                            isRoleChangePending ||
                            assignRole.isPending ||
                            modulesLoading ||
                            currentUser?.id === user.id
                          }
                        >
                          <SelectTrigger
                            className="w-[130px]"
                            aria-label={`Company role for ${user.username}`}
                            title={
                              currentUser?.id === user.id
                                ? 'You cannot change your own company role'
                                : undefined
                            }
                          >
                            <SelectValue>{getRoleBadge(user.role)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <CompanyRoleSelectItems />
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {user.is_active ? (
                          <Badge className="bg-green-600">Active</Badge>
                        ) : (
                          <Badge variant="destructive">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.has_signing_pin ? (
                          <Badge className="bg-green-600">Set</Badge>
                        ) : (
                          <Badge variant="outline">Not Set</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDateTimeInTimeZone(user.date_joined, orgTimeZone)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setPinDialogUser({ id: user.id, username: user.username });
                              setPinValue('');
                              setPinError(null);
                            }}
                          >
                            <KeyRound className="h-4 w-4 mr-1" />
                            {user.has_signing_pin ? 'Change PIN' : 'Set PIN'}
                          </Button>
                          {user.has_signing_pin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setClearPinUser({ id: user.id, username: user.username })}
                            >
                              Clear PIN
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={
                              currentUser?.id === user.id ||
                              user.is_organization_owner
                            }
                            title={
                              currentUser?.id === user.id
                                ? 'You cannot delete your own account'
                                : user.is_organization_owner
                                  ? 'The organization owner cannot be deleted'
                                  : undefined
                            }
                            onClick={() => {
                              setNukeTarget({ id: user.id, username: user.username, email: user.email });
                              setNukeOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {users.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-muted-foreground">
              Showing {users.length} of {usersData?.count || 0} users
            </p>
          )}
        </CardContent>
      </Card>
      <AlertDialog open={nukeOpen} onOpenChange={setNukeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Only use this for a person created by mistake who has no safety, assignment, or timesheet records. For a leaver, set their H3Crew status to Terminated instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={nukeUser.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleNuke}
              disabled={nukeUser.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {nukeUser.isPending ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!adminPromotion}
        onOpenChange={(open) => {
          if (!open) setAdminPromotion(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote user to Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Admins have full access to company settings, users, and every enabled module.
              Promote {adminPromotion?.user.username} only if they should have that access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRoleChangePending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isRoleChangePending}
              onClick={() => {
                if (!adminPromotion) return;
                const { user } = adminPromotion;
                setAdminPromotion(null);
                void applyRoleChange(user, 'Admin', true);
              }}
            >
              {isRoleChangePending ? 'Promoting...' : 'Promote to Admin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ModuleRoleSyncDialog
        open={!!roleSync}
        userName={roleSync?.userName ?? ''}
        newRole={roleSync?.newRole ?? 'Viewer'}
        modules={topLevelModules}
        currentRoles={roleSync?.currentRoles ?? {}}
        isUpdating={updateModuleRoles.isPending}
        onUpdate={handleUpdateModules}
        onKeep={() => {
          setRoleSync(null);
          if (pendingMfaWarning) {
            toast.warning(pendingMfaWarning, { duration: 10000 });
          }
          setPendingMfaWarning(null);
        }}
      />

      <Dialog open={!!pinDialogUser} onOpenChange={(open) => { if (!open) setPinDialogUser(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{pinDialogUser?.username ? `Set PIN for ${pinDialogUser.username}` : 'Set PIN'}</DialogTitle>
            <DialogDescription>Enter a 6-digit signing PIN for kiosk JSA signing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="admin-pin">6-Digit PIN *</Label>
            <Input
              id="admin-pin"
              type="tel"
              inputMode="numeric"
              maxLength={6}
              pattern="[0-9]{6}"
              autoComplete="off"
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit PIN"
              required
            />
            {pinError && <p className="text-sm text-red-600">{pinError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDialogUser(null)}>Cancel</Button>
            <Button
              data-validate-required="true"
              onClick={handleSetPin}
              disabled={setPinMutation.isPending}
            >
              {setPinMutation.isPending ? 'Setting...' : 'Set PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!clearPinUser} onOpenChange={(open) => { if (!open) setClearPinUser(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Signing PIN</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the signing PIN for <strong>{clearPinUser?.username}</strong>. They will not be able to use PIN-based kiosk signing until a new PIN is set.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearPinMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearPin} disabled={clearPinMutation.isPending}>
              {clearPinMutation.isPending ? 'Clearing...' : 'Clear PIN'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
